import React, { useEffect, useRef, useState } from 'react';
import { FaRegCalendarAlt, FaUndo } from 'react-icons/fa';

const toInputDate = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
};

const monthsAgo = (months) => {
  const date = new Date();
  const dayOfMonth = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() - months);
  const daysInTargetMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(dayOfMonth, daysInTargetMonth));
  return date;
};

const PRESETS = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
];

const short = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '';

const describe = (range) => {
  if (!range) return 'Page range';
  if (!range.startDate && !range.endDate) return 'All time';
  if (!range.startDate) return `Up to ${short(range.endDate)}`;
  if (!range.endDate) return `From ${short(range.startDate)}`;
  return `${short(range.startDate)} – ${short(range.endDate)}`;
};

/**
 * Per-card range override. `null` means the card follows the page filter, which
 * is the default and the only state that costs no extra request - an override
 * refetches just this card from its own endpoint.
 */
export default function CardDateFilter({ value, onChange, busy = false }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrapper.current && !wrapper.current.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const apply = (next) => {
    onChange(next);
    setOpen(false);
  };

  const overridden = Boolean(value);

  return (
    <div ref={wrapper} className="relative mt-1.5 inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] transition-colors ${
          overridden
            ? 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100'
            : 'border-transparent text-slate-400 hover:border-slate-200 hover:text-slate-600'
        }`}
      >
        <FaRegCalendarAlt className="h-2.5 w-2.5" />
        <span>{describe(value)}</span>
        {busy && <span className="h-1 w-1 animate-pulse rounded-full bg-sky-500" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Date range for this graph"
          className="absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-slate-200 bg-white p-2.5 shadow-lg"
        >
          <div className="mb-2 flex flex-wrap gap-1">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() =>
                  apply({ startDate: toInputDate(monthsAgo(preset.months)), endDate: toInputDate(new Date()) })
                }
                className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => apply({ startDate: '', endDate: '' })}
              className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
            >
              All
            </button>
          </div>

          <label className="block text-[11px] text-slate-500">
            From
            <input
              type="date"
              value={value?.startDate || ''}
              onChange={(event) =>
                onChange({ startDate: event.target.value, endDate: value?.endDate || '' })
              }
              className="mt-0.5 w-full rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-700 focus:border-sky-400 focus:outline-none"
            />
          </label>

          <label className="mt-1.5 block text-[11px] text-slate-500">
            To
            <input
              type="date"
              value={value?.endDate || ''}
              onChange={(event) =>
                onChange({ startDate: value?.startDate || '', endDate: event.target.value })
              }
              className="mt-0.5 w-full rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-700 focus:border-sky-400 focus:outline-none"
            />
          </label>

          {overridden && (
            <button
              type="button"
              onClick={() => apply(null)}
              className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
            >
              <FaUndo className="h-2.5 w-2.5" />
              Follow page filter
            </button>
          )}
        </div>
      )}
    </div>
  );
}
